/**
 * LRU (Least Recently Used) Cache for AudioBuffers.
 */
export class LRUCache {
  /**
   * @param {number} capacity Maximum number of items in the cache (default: 50).
   */
  constructor(capacity = 50) {
    if (capacity <= 0) {
      throw new Error('LRUCache capacity must be greater than 0');
    }
    this.capacity = capacity;
    this.cache = new Map();
  }

  /**
   * Get item from cache and mark as recently used.
   * @param {string} key
   * @returns {*} cached item or undefined
   */
  get(key) {
    if (!this.cache.has(key)) {
      return undefined;
    }
    const value = this.cache.get(key);
    // Refresh position (delete and re-set to make it most recently used)
    this.cache.delete(key);
    this.cache.set(key, value);
    return value;
  }

  /**
   * Put item into cache. Evicts LRU item if capacity is exceeded.
   * @param {string} key
   * @param {*} value
   */
  set(key, value) {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.capacity) {
      // First key in Map iterator is the least recently used
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
      }
    }
    this.cache.set(key, value);
  }

  /**
   * Check if key exists in cache without updating LRU order.
   * @param {string} key
   * @returns {boolean}
   */
  has(key) {
    return this.cache.has(key);
  }

  /**
   * Delete key from cache.
   * @param {string} key
   * @returns {boolean}
   */
  delete(key) {
    return this.cache.delete(key);
  }

  /**
   * Clear all items from cache.
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Current number of entries in cache.
   * @returns {number}
   */
  get size() {
    return this.cache.size;
  }
}

export default LRUCache;
